import type { EventBus } from '@hc-frontend/core-events';
import type { ZipCode } from '@hc-frontend/core-types';
import type { SubmitFormEvent } from '@hc-frontend/core-ui';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import type { Optional } from '@hc-frontend/core-utils-types';
import type { FormElement } from '@hc-frontend/third-party-snowplow';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';

import type { SubmitZipPlateTrackingRepository } from '../../domain/repositories/submit-zip-plate-tracking-repository';

export interface TrackSubmitZipPlateAdapterFactoryOptions {
  eventBus: EventBus;
  trackingContexts: SelfDescribingJson[];
}

const buildFormElements = (zipCode: Optional<ZipCode>) => {
  const value = zipCode ?? null;

  const element: FormElement = {
    name: 'ZIP_CODE',
    value,
    nodeName: 'INPUT',
  };
  return [element];
};

export function trackSubmitZipPlateAdapterFactory({
  eventBus,
  trackingContexts,
}: TrackSubmitZipPlateAdapterFactoryOptions): Pick<
  SubmitZipPlateTrackingRepository,
  'trackSubmitZipPlate'
> {
  return {
    trackSubmitZipPlate: async ({ plateName, zipCode }) => {
      try {
        const formElements = buildFormElements(zipCode);
        const formId = plateName;

        eventBus.publish<
          SubmitFormEvent,
          (EventTarget & HTMLFormElement) | null
        >({
          id: formId,
          topic: 'core.ui.form.submit',
          source: null,
          submitForm: {
            id: formId,
            elements: formElements,
          },
          contexts: trackingContexts,
        });

        return createSuccessOperationResult(undefined);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            { code: 'failedToTrackSubmitZipPlate', rootCause: error as Error },
          ],
        });
      }
    },
  };
}
